import { Editor, Node, NodeEntry, Path, Transforms } from 'slate'

import { BLOCK_FORMATS, CustomEditor } from 'core-components/library/richTextEditor'

import { isFirstChildSelectedHelper } from '../isFirstChildSelectedHelper'

import { increaseIndent } from './../../toolbar/components/indent/indentButtons'
import { isList, isListItem } from './isListHelper'
import { mergeWithPreviousSubList } from './mergeWithPreviousSubList'
import { getChildSubListItemRef, moveChildSubListUnderItem } from './moveChildSubList'

const isNextElementInSameList = (editor: CustomEditor, endNodeListItem: NodeEntry) => {
  const nextElementPath = Path.next(endNodeListItem[1])
  return Node.has(editor, nextElementPath)
}

const isNestedListItem = (editor: CustomEditor, endNodeListItem: NodeEntry) => {
  const nestedListPath = Path.parent(endNodeListItem[1])
  return !!Editor.above(editor, { at: nestedListPath, match: isListItem })
}

const prepareNextList = (
  editor: CustomEditor,
  endNodeListItem: NodeEntry,
  startNodeListItem: NodeEntry,
  format: BLOCK_FORMATS
) => {
  const startLength = startNodeListItem[1].length
  const endLength = endNodeListItem[1].length

  const isListNestingDifference = endLength - startLength

  // If the beginning and end of the list are at the same nesting level, then
  // there is no need to do the following processing, because it is enough to simply wrap the selected elements
  if (isListNestingDifference) {
    Transforms.splitNodes(editor, { at: Path.next(endNodeListItem[1]) })

    const [currentList] = Editor.nodes(editor, {
      at: endNodeListItem[1],
      match: isList,
      mode: 'lowest',
    })
    const nextListPath = Path.next(currentList[1])

    const [nextList] = Editor.nodes(editor, { at: nextListPath, match: isList, mode: 'lowest' })
    if (nextList) {
      Transforms.wrapNodes(
        editor,
        { type: BLOCK_FORMATS.listItem, children: [] },
        {
          at: nextListPath,
        }
      )

      const newPath = nextListPath.slice(0, startNodeListItem[1].length)

      // In case of increasing the nesting level of the list, we will wrap the selected
      // elements in the list, but before that we move and wrap the rest of the list in the
      // list so that it retains its original nesting level
      Transforms.moveNodes(editor, { at: nextListPath, to: Path.next(newPath) })
      for (let i = 0; i < isListNestingDifference / 2 - 1; i++) {
        Transforms.wrapNodes(editor, { type: format, children: [] }, { at: Path.next(newPath) })
        Transforms.wrapNodes(
          editor,
          { type: BLOCK_FORMATS.listItem, children: [] },
          {
            at: Path.next(newPath),
          }
        )
      }
    }
  }
}

export const increaseListLevel = (editor: CustomEditor, format: BLOCK_FORMATS) => {
  const selection = editor.selection

  if (!selection) {
    return
  }

  const isFirstChildSelected = isFirstChildSelectedHelper(editor)

  // Have to insert break before selected list items
  if (isFirstChildSelected) {
    increaseIndent(editor)

    return
  }

  const { anchor, focus } = selection

  const isOneElementSelected = Path.equals(anchor.path, focus.path)

  const startNodePath = Path.isBefore(anchor.path, focus.path) ? anchor.path : focus.path
  const endNodePath = Path.isAfter(anchor.path, focus.path) ? anchor.path : focus.path

  const startNodeListItem = Editor.above(editor, { at: startNodePath, match: isListItem })
  const endNodeListItem = Editor.above(editor, { at: endNodePath, match: isListItem })

  // The items nested under the last selected one are not a part of the selection,
  // so they have to be moved along with it to stay one level deeper than it
  const childSubListItemRef = getChildSubListItemRef(editor, endNodeListItem?.[1])
  const endListItemRef = endNodeListItem ? Editor.pathRef(editor, endNodeListItem[1]) : undefined

  if (!isOneElementSelected && startNodeListItem && endNodeListItem) {
    if (
      isNextElementInSameList(editor, endNodeListItem) &&
      isNestedListItem(editor, endNodeListItem)
    ) {
      prepareNextList(editor, endNodeListItem, startNodeListItem, format)
    }
  }

  Transforms.wrapNodes(editor, { type: format, children: [] }, { match: isListItem })
  Transforms.wrapNodes(
    editor,
    { type: BLOCK_FORMATS.listItem, children: [] },
    {
      match: isList,
      at: Editor.start(editor, selection).path,
    }
  )

  // The list item wrapping the created list keeps the selected items,
  // so it is a grandparent of the last of them
  const lastListItemPath = endListItemRef?.current

  // The items have been moved to a deeper level, where they have to continue the
  // nested list already existing right before them instead of starting a list of their own
  if (lastListItemPath) {
    mergeWithPreviousSubList(editor, Path.parent(Path.parent(lastListItemPath)))
  }

  moveChildSubListUnderItem(editor, endListItemRef, childSubListItemRef)
}
